import * as React from "react";
import {useRef} from "react";
import {KatInput} from "@amzn/katal-react";

export interface ActivitySearchCriteria {
    login: string;    // auditor alias (QA/Manager only)
    caseId: string;   // Case ID
    dpId: string;     // DP ID (referenceId)
}

interface ActivitySearchProps {
    canSearchByLogin: boolean;   // from server features
    alias: string;
    /** Reports the current field values as the user types or pastes (parent owns the criteria). */
    onChange: (criteria: ActivitySearchCriteria) => void;
    /** Enter key triggers the single shared Search shown in the combined filter bar. */
    onEnter: () => void;
}

/**
 * Login / Case ID / DP ID inputs for the Case Audit Overview. These are rendered inside the
 * combined "Search & Filters" bar alongside the case FilterBar and share a single Search /
 * Reset button pair (owned by the parent), so there is only one Search button on screen.
 * - Login: enabled only when `canSearchByLogin`; otherwise disabled + prefilled with the
 *   caller's own alias (auditors are alias-locked, HLD §6.8).
 */
const ActivitySearch = (props: ActivitySearchProps) => {
    const loginRef = useRef<any>(null);
    const caseIdRef = useRef<any>(null);
    const dpIdRef = useRef<any>(null);

    const canSearchByLogin = props.canSearchByLogin;

    const report = () => {
        props.onChange({
            login: canSearchByLogin ? (loginRef.current?.value || '') : '',
            caseId: caseIdRef.current?.value || '',
            dpId: dpIdRef.current?.value || ''
        });
    };

    const onKeyup = (e: any) => {
        report();
        if (e.key === 'Enter') props.onEnter();
    };

    return (
        <div className='activity-search-fields' data-testid='activity-search'>
            <KatInput ref={loginRef} type='search' label='Login' placeholder='Search Login'
                      className='search-field-input' onKeyup={onKeyup} onChange={report}
                      disabled={!canSearchByLogin}
                      value={!canSearchByLogin ? props.alias : undefined}
                      data-testid='search-login'/>
            <KatInput ref={caseIdRef} type='search' label='Case ID' placeholder='Search Case ID'
                      className='search-field-input' onKeyup={onKeyup} onChange={report}
                      data-testid='search-case-id'/>
            <KatInput ref={dpIdRef} type='search' label='DP ID' placeholder='Search DP ID'
                      className='search-field-input' onKeyup={onKeyup} onChange={report}
                      data-testid='search-dp-id'/>
        </div>
    );
};

export default ActivitySearch;
